import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { Button } from "../ui/button";
import { RankChange } from "../rank/RankChange";
import { MEDALS, gameWinners } from "../../lib/roundSummary";
import { t, tList, textDir, useLang } from "../../lib/i18n";

// ---------------------------------------------------------------------------
// Fireworks, made to stay smooth on a phone:
// - a spark is a small glow picture drawn once per colour, then stamped with
//   drawImage and added up ("lighter"). No shadowBlur: blurring every spark on
//   every frame brought the screen down to a few frames a second;
// - the trails come from fading the last frame a little (one fill), not from a
//   list of past positions per spark;
// - the motion follows the clock, not the frame count: a slow phone skips
//   frames, but the show keeps its speed;
// - bursts go where the card leaves room (above and below it on a phone,
//   beside it on a wide screen). The show calms down, then ends, and the loop
//   stops.
// ---------------------------------------------------------------------------
const PALETTES = [
  [[251, 191, 36], [245, 158, 11], [253, 230, 138], [255, 247, 214]],   // Gold
  [[244, 114, 182], [236, 72, 153], [253, 164, 175], [255, 228, 236]],  // Rose
  [[96, 165, 250], [59, 130, 246], [147, 197, 253], [219, 234, 254]],   // Blue
  [[52, 211, 153], [16, 185, 129], [110, 231, 183], [209, 250, 229]],   // Emerald
  [[167, 139, 250], [139, 92, 246], [196, 181, 253], [237, 233, 254]],  // Purple
  [[251, 146, 60], [249, 115, 22], [253, 186, 116], [255, 237, 213]],   // Orange
  [[255, 255, 255], [226, 232, 240], [203, 213, 225], [186, 230, 253]], // Silver
];
const GOLD = 0;
const SILVER = 6;
const KINDS = ["peony", "peony", "pistil", "ring", "willow"];

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (list) => list[Math.floor(Math.random() * list.length)];

// A glow dot: white-hot centre, coloured halo, transparent edge.
function glowSprite([r, g, b], size, core = 0.18) {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const x = c.getContext("2d");
  const half = size / 2;
  const grad = x.createRadialGradient(half, half, 0, half, half, half);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(core, `rgba(${r},${g},${b},1)`);
  grad.addColorStop(0.42, `rgba(${r},${g},${b},0.32)`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  x.fillStyle = grad;
  x.fillRect(0, 0, size, size);
  return c;
}

function FireworksCanvas({ avoidRef }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    // Reduced motion: three soft bursts, no rockets, no flicker.
    const calm = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const sprites = PALETTES.map((pal) => pal.map((c) => glowSprite(c, 64)));
    const flashes = PALETTES.map((pal) => glowSprite(pal[3], 128, 0.06));
    const head = glowSprite([255, 236, 190], 64, 0.32);
    const trail = [sprites[GOLD][0], sprites[GOLD][2], sprites[SILVER][0]];

    let w = 0, h = 0, unit = 0, size = 0, density = 1;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      unit = Math.min(w, h);
      size = Math.min(14, Math.max(7, unit * 0.02)); // a spark, in CSS px
      density = Math.min(1.7, Math.max(0.85, (w * h) / (390 * 844)));
    };
    resize();
    window.addEventListener("resize", resize);

    const sparks = [];
    const rockets = [];
    const maxSparks = Math.round(650 * density);
    const add = (p) => { if (sparks.length < maxSparks) sparks.push(p); };
    // drag = share of its speed a spark keeps after one second.
    const spark = (x, y, vx, vy, img, sz, life, drag, gravity, strobe) =>
      add({ x, y, vx, vy, img, size: sz, life, max: life, drag, gravity, strobe: strobe && !calm, phase: Math.random() });

    // Where a burst can be seen: around the card, not behind it.
    const cardRect = () => {
      const r = avoidRef?.current?.getBoundingClientRect();
      return r && r.width > 0 ? r : null;
    };
    const pickSpot = () => {
      const r = cardRect();
      const m = unit * 0.06;
      if (!r) return { x: rand(0.12, 0.88) * w, y: rand(0.08, 0.5) * h };
      const zones = [
        { x0: m, x1: w - m, y0: m * 0.7, y1: r.top - 8, weight: 1.6 },         // above the card
        { x0: m, x1: w - m, y0: r.bottom + 8, y1: h - m, weight: 0.9 },        // below it
        { x0: m * 0.7, x1: r.left - 8, y0: m, y1: r.bottom, weight: 1 },       // left
        { x0: r.right + 8, x1: w - m * 0.7, y0: m, y1: r.bottom, weight: 1 },  // right
      ].filter((z) => z.x1 - z.x0 > 24 && z.y1 - z.y0 > 24);
      if (!zones.length) return { x: rand(0.15, 0.85) * w, y: rand(0.06, 0.2) * h };
      const area = (z) => (z.x1 - z.x0) * (z.y1 - z.y0) * z.weight;
      let roll = Math.random() * zones.reduce((sum, z) => sum + area(z), 0);
      const zone = zones.find((z) => (roll -= area(z)) <= 0) || zones[0];
      return { x: rand(zone.x0, zone.x1), y: rand(zone.y0, zone.y1) };
    };
    const topCentre = () => {
      const r = cardRect();
      return { x: w / 2, y: r && r.top > 60 ? Math.max(unit * 0.1, r.top * 0.5) : h * 0.18 };
    };

    // peony: a sphere of sparks · ring: a tilted circle · willow: long gold
    // sparks that droop and crackle · pistil: a peony with a core of another colour.
    function explode(x, y, kind, pal, scale = 1) {
      const set = sprites[pal];
      add({ x, y, vx: 0, vy: 0, img: flashes[pal], size: unit * 0.42 * scale, life: 0.24, max: 0.24, flash: true });
      if (kind === "ring") {
        const n = Math.round(44 * Math.sqrt(density) * scale);
        const reach = unit * rand(0.17, 0.23) * scale * 2.8;
        const tilt = rand(0, Math.PI), squash = rand(0.35, 1);
        const ct = Math.cos(tilt), st = Math.sin(tilt);
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2;
          const ex = Math.cos(a) * reach, ey = Math.sin(a) * reach * squash;
          spark(x, y, ex * ct - ey * st, ex * st + ey * ct, set[i % 2], size * rand(0.9, 1.15),
            rand(1.1, 1.4), Math.exp(-2.8), unit * 0.28, false);
        }
        return;
      }
      const willow = kind === "willow";
      const k = willow ? 1.5 : 2.8; // a spark travels speed / k before it stops
      const reach = unit * (willow ? rand(0.2, 0.25) : rand(0.2, 0.27)) * scale * k;
      const n = Math.round(72 * density * scale);
      for (let i = 0; i < n; i++) {
        // A point on a sphere seen from afar: denser on the rim, like a real shell.
        const a = Math.random() * Math.PI * 2;
        const z = rand(-1, 1);
        const v = Math.sqrt(1 - z * z) * reach * rand(0.94, 1.04);
        spark(x, y, Math.cos(a) * v, Math.sin(a) * v,
          willow ? set[i % 3 === 0 ? 2 : 0] : set[i % 4 === 3 ? 3 : i % 3],
          size * (willow ? rand(0.7, 0.95) : rand(0.85, 1.15)),
          willow ? rand(2, 2.8) : rand(1, 1.5),
          Math.exp(-k), unit * (willow ? 0.42 : 0.3),
          Math.random() < (willow ? 0.4 : 0.12));
      }
      if (kind === "pistil") {
        const core = sprites[pal === GOLD ? SILVER : GOLD];
        for (let i = 0; i < n * 0.3; i++) {
          const a = Math.random() * Math.PI * 2;
          const v = rand(0.6, 1) * reach * 0.38;
          spark(x, y, Math.cos(a) * v, Math.sin(a) * v, core[i % 3], size * 0.85,
            rand(0.7, 1), Math.exp(-2.8), unit * 0.3, false);
        }
      }
    }

    // A rocket rises from the bottom edge and bursts at the top of its climb.
    function launch(kind, pal, scale) {
      const to = pickSpot();
      const x = to.x + rand(-0.05, 0.05) * w, y = h + 6;
      const T = Math.min(1.35, Math.max(0.6, 0.5 + ((y - to.y) / h) * 0.8));
      const vy = (-2 * (y - to.y)) / T;
      rockets.push({ x, y, vx: (to.x - x) / T, vy, g: -vy / T, t: 0, T, emit: 0, kind, pal, scale });
    }

    // The show: bursts in place as the card arrives, a steady rhythm of
    // rockets, a calmer end, then nothing.
    const opening = calm
      ? [[0.1, "peony", GOLD, 1.1], [0.9, "ring", 2, 0.9], [1.8, "willow", GOLD, 1]]
      : [[0.05, "peony", GOLD, 1.25], [0.35, "pistil", 1, 0.9], [0.55, "peony", 2, 0.9],
         [0.95, "ring", 3, 1], [1.3, "peony", 4, 0.95], [1.6, "pistil", 5, 0.9], [2.1, "willow", GOLD, 1.15]];
    const steadyEnd = calm ? 0 : 13;
    const showEnd = calm ? 0 : 21;
    let opened = 0;
    let nextLaunch = 2.8;
    let elapsed = 0;
    let last = performance.now();
    let raf = 0;

    function frame(now) {
      const dt = Math.min(1 / 15, Math.max(0.001, (now - last) / 1000));
      last = now;
      elapsed += dt; // stands still while the tab is hidden: no backlog when it comes back

      while (opened < opening.length && opening[opened][0] <= elapsed) {
        const [, kind, pal, scale] = opening[opened];
        const at = opened === 0 ? topCentre() : pickSpot();
        explode(at.x, at.y, kind, pal, scale);
        opened++;
      }
      if (elapsed >= nextLaunch && elapsed < showEnd) {
        const steady = elapsed < steadyEnd;
        launch(pick(KINDS), Math.floor(Math.random() * PALETTES.length), rand(0.8, 1.1));
        if (steady && Math.random() < 0.3) launch(pick(KINDS), Math.floor(Math.random() * PALETTES.length), rand(0.75, 1));
        nextLaunch = elapsed + (steady ? rand(0.8, 1.3) : rand(1.6, 2.4));
      }

      // Fade what was drawn (the trails), then add this frame's light.
      ctx.globalCompositeOperation = "destination-out";
      ctx.globalAlpha = 1;
      ctx.fillStyle = `rgba(0,0,0,${1 - Math.pow(0.78, dt * 60)})`;
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = "lighter";

      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.t += dt;
        r.vy += r.g * dt;
        r.x += r.vx * dt;
        r.y += r.vy * dt;
        for (r.emit += dt * 55; r.emit >= 1; r.emit--) {
          spark(r.x + rand(-1.5, 1.5), r.y + rand(0, 3), rand(-18, 18), rand(10, 50), pick(trail),
            size * rand(0.45, 0.7), rand(0.3, 0.6), 0.3, unit * 0.25, Math.random() < 0.3);
        }
        ctx.globalAlpha = 1;
        ctx.drawImage(head, r.x - size * 0.7, r.y - size * 0.7, size * 1.4, size * 1.4);
        if (r.t >= r.T) {
          explode(r.x, r.y, r.kind, r.pal, r.scale);
          rockets[i] = rockets[rockets.length - 1];
          rockets.pop();
        }
      }

      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.life -= dt;
        if (p.life <= 0) {
          sparks[i] = sparks[sparks.length - 1];
          sparks.pop();
          continue;
        }
        const f = p.life / p.max; // 1 → 0
        let alpha, sz, px, py;
        if (p.flash) {
          alpha = f * f;
          sz = p.size * (1.15 - 0.4 * f);
        } else {
          px = p.x;
          py = p.y;
          const keep = Math.pow(p.drag, dt);
          p.vx *= keep;
          p.vy = p.vy * keep + p.gravity * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          alpha = Math.min(1, f * 2.2);
          // Crackle: near the end of its life, the spark flickers.
          if (p.strobe && f < 0.35 && (elapsed * 8 + p.phase) % 1 > 0.55) alpha *= 0.3;
          sz = p.size * (0.5 + 0.5 * f);
        }
        ctx.globalAlpha = alpha;
        ctx.drawImage(p.img, p.x - sz / 2, p.y - sz / 2, sz, sz);
        // A fast spark between two far-apart frames: one more stamp halfway,
        // or its trail breaks into dots.
        if (px !== undefined && (p.x - px) ** 2 + (p.y - py) ** 2 > sz * sz * 0.36) {
          ctx.drawImage(p.img, (p.x + px) / 2 - sz / 2, (p.y + py) / 2 - sz / 2, sz, sz);
        }
      }

      if (elapsed > showEnd && opened >= opening.length && !sparks.length && !rockets.length) {
        ctx.clearRect(0, 0, w, h); // the show is over: the loop stops
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [avoidRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      // ph-no-capture: PostHog's session recording leaves it out (it copied the
      // canvas over and over, as costly as the fireworks themselves).
      className="ph-no-capture fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 48 }}
    />
  );
}

const SUITS = ["♠", "♥", "♦", "♣"];

// Star sparkle around the trophy (CSS animation `winner-sparkle`, index.css)
function Sparkle({ angle, dist, delay, cycle }) {
  const x = Math.cos(angle) * dist;
  const y = Math.sin(angle) * dist;
  return (
    <span
      className="winner-sparkle absolute text-amber-300 text-xs pointer-events-none select-none"
      style={{
        top: `calc(50% + ${y}px)`,
        left: `calc(50% + ${x}px)`,
        transform: "translate(-50%,-50%)",
        animationDelay: `${delay}s`,
        animationDuration: `${cycle}s`,
      }}
    >
      ✦
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function WinnerCelebration({
  game,
  me,
  onBackToLobby,
  loading = false,
  onRematch,
  rematching = false,
  rematchHint = "",
}) {
  const { dir } = useLang();
  // The punchlines live in the dictionaries (winner.funny); the draw is kept
  // as an index so the same slot is shown if the language changes.
  const [funnySeed] = useState(() => Math.floor(Math.random() * 1000));
  const cardRef = useRef(null); // the fireworks burst around it

  if (!game || game.phase !== "game_over") return null;
  // Both winners of a table of 4 get this screen; the second's says so (its punchlines would say « you 1, the others 0 »).
  const winners = gameWinners(game);
  const myPlace = me ? winners.indexOf(me.user_id) : -1;
  if (myPlace === -1) return null;
  const second = myPlace === 1;

  const winner = game.players?.find((p) => p.user_id === me.user_id);
  const punchlines = tList("winner.funny");
  const funnyText = second ? t("winner.secondLine") : punchlines[funnySeed % punchlines.length];
  const placeOf = (p) => {
    const i = winners.indexOf(p.user_id);
    return i === -1 ? winners.length : i;
  };
  const sorted = (game.players ?? []).slice().sort((a, b) => placeOf(a) - placeOf(b) || a.total_score - b.total_score);

  const medals = ["🥇", "🥈", "🥉"];
  const withMedal = winners.length > 1 ? winners.length : medals.length; // no bronze when only two have won
  const sparkleAngles = Array.from({ length: 7 }, (_, i) => ({
    angle: (Math.PI * 2 * i) / 7 - Math.PI / 2,
    dist:  44,
    delay: 0.7 + i * 0.22,
    cycle: 2.9 + (i % 3) * 0.3, // not all in step
  }));

  return (
    // A night sky over the game, painted once: no backdrop blur, which had to be
    // redone on every frame of the fireworks.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="winner-screen"
      data-place={myPlace + 1}
      style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(15, 23, 42, 0.95), rgba(2, 6, 23, 0.98) 70%)" }}
    >
      <FireworksCanvas avoidRef={cardRef} />

      {/* Modal */}
      <motion.div
        initial={{ scale: 0.45, opacity: 0, y: 70 }}
        animate={{ scale: 1,    opacity: 1, y: 0  }}
        transition={{ type: "spring", stiffness: 190, damping: 18, delay: 0.15 }}
        dir={dir}
        className="relative z-50 w-full max-w-sm mx-4"
      >
        {/* Outer glow halo: a gradient, not a blur filter */}
        <div
          className="absolute -inset-10 pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(251, 191, 36, 0.14), transparent)" }}
        />

        <div
          ref={cardRef}
          className="relative bg-gradient-to-b from-slate-900/[0.88] via-slate-900/[0.84] to-slate-950/[0.92] border border-amber-400/30 rounded-3xl overflow-hidden shadow-[0_0_90px_rgba(251,191,36,0.18)]"
        >

          {/* Top shimmer bar */}
          <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-amber-400 to-transparent" />

          {/* ── Header ── */}
          <div className="px-6 pt-6 pb-4 text-center">

            {/* Suit row */}
            <div className="flex justify-center gap-3 mb-4">
              {SUITS.map((s, i) => (
                <motion.span
                  key={s}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + i * 0.07 }}
                  className={`text-lg ${s === "♥" || s === "♦" ? "text-red-400/60" : "text-slate-300/50"}`}
                >
                  {s}
                </motion.span>
              ))}
            </div>

            {/* Trophy + sparkles */}
            <div className="relative inline-block mb-3">
              {sparkleAngles.map((sp, i) => <Sparkle key={i} {...sp} />)}
              <div className="winner-trophy text-[64px] leading-none select-none">{second ? MEDALS[1] : "🏆"}</div>
            </div>

            {/* CHAMPION heading */}
            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.48 }}
              className="font-display text-4xl sm:text-5xl italic text-amber-300 leading-none drop-shadow-[0_0_28px_rgba(251,191,36,0.55)]"
            >
              {second ? t("winner.second") : t("winner.champion")}
            </motion.h1>

            {/* Winner name */}
            {winner && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65 }}
                className="mt-1.5"
              >
                <span className="text-white font-bold text-base">{winner.name}</span>
              </motion.div>
            )}

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75 }}
              className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-500/55 mt-0.5"
            >
              {second ? t("winner.secondSubtitle") : t("winner.subtitle")}
            </motion.p>

            {/* Separator */}
            <div className="flex items-center gap-2 my-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-amber-500/30" />
              <span className="text-amber-500/40 text-[11px]">✦</span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-amber-500/30" />
            </div>

            {/* Funny message */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-slate-300/90 text-sm italic leading-relaxed"
            >
              {funnyText}
            </motion.p>
          </div>

          {/* ── Ranking ── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="mx-4 mb-4 bg-slate-800/55 border border-white/[0.05] rounded-2xl p-3 space-y-1.5"
          >
            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500 mb-2.5 text-center">
              {t("winner.ranking")}
            </div>

            {sorted.map((p, i) => {
              const isWinner = winners.includes(p.user_id);
              const isMe     = p.user_id === me?.user_id;
              return (
                <div
                  key={p.user_id}
                  className={`flex items-center justify-between text-sm rounded-xl px-3 py-1.5 transition-colors ${
                    isWinner
                      ? "bg-amber-500/15 border border-amber-400/30 text-amber-300 font-bold"
                      : "text-slate-400"
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] w-5 shrink-0">
                      {i < withMedal ? medals[i] : `${i + 1}.`}
                    </span>
                    <span className="truncate max-w-[110px]">
                      {isMe ? t("common.you") : p.name}
                    </span>
                    {p.eliminated && !isWinner && (
                      <span className="text-[9px] text-red-400/55 shrink-0">{t("winner.eliminated")}</span>
                    )}
                  </span>
                  <span className="tabular-nums text-xs shrink-0 ms-2">{t("common.pts", { n: p.total_score })}</span>
                </div>
              );
            })}
          </motion.div>

          {/* ── What the game won the player for their rank ── */}
          {game.rank_results?.[me.user_id] && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
              className="mx-4 mb-4"
            >
              <RankChange result={game.rank_results[me.user_id]} />
            </motion.div>
          )}

          {/* ── CTA ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35 }}
            className="px-4 pb-5 space-y-2"
          >
            {rematchHint && (
              <p
                dir={textDir(rematchHint)}
                data-testid="rematch-hint"
                className="text-center text-xs font-medium text-emerald-300"
              >
                {rematchHint}
              </p>
            )}
            <Button
              onClick={onRematch}
              disabled={loading || rematching}
              data-testid="rematch-btn"
              className="w-full bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 font-bold rounded-full py-5 text-sm shadow-lg shadow-amber-500/30 disabled:opacity-50 transition-all"
            >
              <RotateCcw className="h-4 w-4" />
              {t("common.rematch")}
            </Button>
            <Button
              onClick={onBackToLobby}
              disabled={loading || rematching}
              data-testid="back-to-lobby-btn"
              className="w-full bg-transparent hover:bg-white/5 text-slate-300 hover:text-white font-semibold rounded-full py-4 text-sm disabled:opacity-50 transition-all"
            >
              {loading ? t("winner.closing") : t("winner.backLobby")}
            </Button>
          </motion.div>

          {/* Bottom accent */}
          <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-amber-400/40 to-transparent" />
        </div>
      </motion.div>
    </div>
  );
}

export default WinnerCelebration;
